"use client";

import { useEffect, useState } from "react";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { UStyles } from "@/app/utils/UStyles";
import { Globals } from "@/app/vars/Globals";
import styles from "./EditableList.module.css";

type Props = {
  items: string[];
  onSave: (newItems: string[]) => void;
  placeholder?: string;
  addItemText?: string;
  maxItems?: number;
  itemMaxLength?: number;
  className?: string;
  listClassName?: string;
  disabled?: boolean;
  hideBullet?: boolean;
  bulletColor?: string;
  lang?: string;
};

export default function EditableList(props: Props) {
  const T = useT();
  const {
    items,
    onSave,
    placeholder,
    addItemText,
    maxItems = Globals.MAX_AVATAR_LIST_ITEMS,
    itemMaxLength,
    className,
    listClassName,
    disabled = false,
    hideBullet = false,
    bulletColor,
  } = props;

  const finalPlaceholder = placeholder || (T(TKey.NEW_DOTS) as string);
  const finalAddItemText = addItemText || (T(TKey.ADD_ITEM) as string);

  const [addingIndex, setAddingIndex] = useState<number | null>(null);
  const [editingStates, setEditingStates] = useState<Record<number, boolean>>({});
  const [localItems, setLocalItems] = useState(items);

  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const handleUpdate = (index: number, newValue: string) => {
    const newItems = [...localItems];
    if (newValue.trim() === "") {
      newItems.splice(index, 1);
    } else {
      newItems[index] = newValue;
    }
    setLocalItems(newItems);
    onSave(newItems);
  };

  const handleDelete = (index: number) => {
    const newItems = [...localItems];
    newItems.splice(index, 1);
    setLocalItems(newItems);
    onSave(newItems);
  };

  const handleAdd = (newValue: string) => {
    setAddingIndex(null);
    if (newValue.trim() !== "") {
      const newItems = [...localItems, newValue];
      setLocalItems(newItems);
      onSave(newItems);
    }
  };

  return (
    <div className={UStyles.classNames(styles.container, className)}>
      <ul className={UStyles.classNames(styles.list, listClassName)}>
        {localItems.map((item, index) => (
          <li key={index} className={styles.listItem}>
            {!hideBullet && (
              <div
                className={styles.bullet}
                style={bulletColor ? { backgroundColor: bulletColor } : undefined}
              />
            )}
            <div className={styles.itemContent}>
              <EditableText
                value={item}
                onSave={(val) => handleUpdate(index, val)}
                multiline={true}
                maxLength={itemMaxLength}
                className={styles.editableItem}
                textClassName={styles.editableText}
                inputClassName={styles.editableText}
                disabled={disabled}
                onEditingChange={(isEditing) =>
                  setEditingStates((prev) => ({ ...prev, [index]: isEditing }))
                }
              />
              {!disabled && !editingStates[index] && (
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => handleDelete(index)}
                  title={T(TKey.DELETE) as string}
                >
                  <IconTrash />
                </button>
              )}
            </div>
          </li>
        ))}
        {addingIndex !== null && (
          <li className={styles.listItem}>
            {!hideBullet && (
              <div
                className={styles.bullet}
                style={bulletColor ? { backgroundColor: bulletColor } : undefined}
              />
            )}
            <div className={styles.itemContent}>
              <EditableText
                value=""
                placeholder={finalPlaceholder}
                onSave={handleAdd}
                onCancel={() => setAddingIndex(null)}
                multiline={true}
                maxLength={itemMaxLength}
                alwaysShowEdit={true}
                startEditingOnMount={true}
                className={styles.editableItem}
                textClassName={styles.editableText}
                inputClassName={styles.editableText}
              />
            </div>
          </li>
        )}
      </ul>

      {!disabled && addingIndex === null && localItems.length < maxItems && (
        <div className={styles.addAction}>
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => setAddingIndex(localItems.length)}
          >
            <IconPlus />
            <span>{finalAddItemText}</span>
          </button>
        </div>
      )}
    </div>
  );
}
